<template>
    <section class="flex flex-col gap-5">
        <p class="sr-only" role="status">{{ sending ? t("reflections.team.sending") : "" }}</p>
        <TeamFeedbackAnimation v-if="sending" @done="onSendAnimationDone" />

        <!-- Feedback submitted state — the prototype drops the header here so the celebration stands alone. -->
        <div v-else-if="submitted" class="flex flex-col items-center gap-4 text-center pt-6">
            <span class="submitted-badge">
                <i class="bi bi-check-lg" aria-hidden="true"></i>
            </span>
            <div class="flex flex-col gap-2 max-w-md">
                <h2 class="display-serif text-ink">{{ t("reflections.team.feedback.submittedTitle") }}</h2>
                <p class="body-small text-ink-muted">
                    {{ subjectFirstName ? t("reflections.team.feedback.submittedBody", { name: subjectFirstName }) : t("reflections.team.feedback.submittedBodyAnonymous") }}
                </p>
            </div>
            <BaseButton theme="primary" size="sm" @click="emit('back')">{{ t("common.done") }}</BaseButton>
        </div>

        <template v-else>
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-3 flex-wrap">
                    <ReflectionIcon kind="team" size="md" />
                    <h2 class="title-subsection text-ink">{{ t("reflections.team.title") }}</h2>
                </div>
                <p class="body-small text-ink-muted">{{ t("reflections.team.description") }}</p>
            </div>

            <HorizontalDivider as="hr" spacing="none" />

            <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("components.assessment.back") }}
            </BaseButton>

            <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
            <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

            <div v-else-if="usesPagedRenderer" class="flex flex-col gap-2.5">
                <div v-if="subjectFirstName" class="scaffold-details-small text-ink-muted">{{ t("reflections.team.giveFeedbackTo", { name: subjectFirstName }) }}</div>

                <AssessmentGroupedSections
                    :response-id="responseId"
                    :questions="questions"
                    :sections="sections"
                    :display-mode="displayMode"
                    :questions-per-page="questionsPerPage"
                    :allow-score-examples="allowScoreExamples"
                    :allow-not-observed="allowNotObserved"
                    :accent-color="ACCENT_MINT"
                    :accent-surface-color="ACCENT_MINT_SURFACE"
                    @submitted="onGroupedSubmitted"
                    @exit="emit('back')" />
            </div>

            <template v-else-if="currentQuestion">
                <div class="flex flex-col gap-2.5">
                    <div v-if="subjectFirstName" class="scaffold-details-small text-ink-muted">{{ t("reflections.team.giveFeedbackTo", { name: subjectFirstName }) }}</div>

                    <QuestionCard
                        :question="currentQuestion"
                        :score="currentSavedScore"
                        :text="currentTextDraft"
                        :not-observed="currentSavedNotObserved"
                        :allow-score-examples="allowScoreExamples"
                        :allow-not-observed="allowNotObserved"
                        :show-unanswered="showValidation && isCurrentUnanswered"
                        :accent-color="ACCENT_MINT"
                        :accent-surface-color="ACCENT_MINT_SURFACE"
                        @update:score="setScore"
                        @update:text="currentTextDraft = $event"
                        @update:not-observed="setNotObserved"
                        @update:busy="composerBusy = $event" />
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

                <div class="flex items-center justify-between">
                    <BaseButton type="button" :disabled="currentIndex === 0 || saving || composerBusy" theme="tertiary" @click="goPrev">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("components.assessment.back") }}
                    </BaseButton>

                    <BaseButton v-if="!isLast" theme="primary" :disabled="saving || composerBusy" @click="goNext"> {{ t("components.assessment.next") }} </BaseButton>
                    <BaseButton v-else theme="primary" :disabled="submitting || saving || composerBusy" @click="askToSubmit">
                        {{ submitting ? t("components.assessment.submitting") : t("reflections.team.feedback.submit") }}
                    </BaseButton>
                </div>

                <div class="body-small text-ink-muted text-center">
                    {{ t("reflections.team.feedback.questionOf", { current: currentIndex + 1, total: questions.length }) }}
                </div>
            </template>

            <SubmitConfirmDialog
                v-model:open="showSubmitConfirm"
                :title="t('components.assessment.submitConfirmTitle')"
                :text="t('reflections.submitConfirm.finalText')"
                :confirm-disabled="submitting"
                @confirm="submit" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import TeamFeedbackAnimation from "~vue/pages/reflections/TeamFeedbackAnimation.vue";
import { DEFAULT_QUESTIONS_PER_PAGE, rendersQuestionPages } from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { isNetworkError } from "~vue/utils/threeSixty";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    inviteToken: string;
    subjectFirstName?: string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{ (event: "back"): void; (event: "submitted"): void }>();

// The team-reflection identity also colors its selected question tiles.
const ACCENT_MINT = "var(--color-chip-team-reflect-text)";
const ACCENT_MINT_SURFACE = "var(--color-chip-team-reflect-bg)";

const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const questionsPerPage = ref(DEFAULT_QUESTIONS_PER_PAGE);
const usesPagedRenderer = computed(() => rendersQuestionPages(displayMode.value));
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const submitted = ref(false);
const sending = ref(false);
const showValidation = ref(false);
const showSubmitConfirm = ref(false);
const composerBusy = ref(false);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.inviteToken}/response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || t("reflections.team.feedback.loadFailed");
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    displayMode.value = payload.display_mode ?? "one_per_page";
    questionsPerPage.value = payload.questions_per_page ?? DEFAULT_QUESTIONS_PER_PAGE;
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}
function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = t("components.assessment.saveFailed");
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

function validate(): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        return false;
    }
    showValidation.value = false;
    return true;
}

async function goNext() {
    if (!validate()) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    if (currentIndex.value < questions.value.length - 1) {
        currentIndex.value += 1;
        currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    }
}

async function goPrev() {
    if (currentIndex.value === 0) {
        emit("back");
        return;
    }
    await saveCurrent();
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

function onSendAnimationDone() {
    sending.value = false;
    submitted.value = true;
}

async function askToSubmit() {
    if (!validate()) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function submit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            showSubmitConfirm.value = false;
            error.value = isNetworkError(fetchError.value)
                ? t("components.assessment.connectionSubmitFailed")
                : (data.value as { error?: string })?.error || t("components.assessment.submitFailed");
            return;
        }
        showSubmitConfirm.value = false;
        sending.value = true;
        emit("submitted");
    } finally {
        submitting.value = false;
    }
}

// The grouped renderer submits the response itself.
function onGroupedSubmitted() {
    submitted.value = true;
    emit("submitted");
}
</script>

<style scoped>
.submitted-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 9999px;
    background: var(--color-chip-status-done-bg);
    color: var(--color-chip-status-done-text);
    font-size: 32px;
}
</style>
